import { useEffect, useMemo, useState } from "react";
import { fetchHomePageData } from "../../fetchHomePageData";
import type { Mentor, MentorPanel } from "../../../shared/mentorTypes";
import { getEmailFromUserId } from "../../../utils/event-helpers";
import { useAppSelector } from "../../../app/store";

const sortAndFilterByIdOrder = (idArray: string[], mentors: Mentor[]) => {
  return mentors
    .filter((obj) => idArray.includes(obj._id))
    .sort((a, b) => idArray.indexOf(a._id) - idArray.indexOf(b._id));
};

export default function useMentorPalData() {
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [panels, setPanels] = useState<MentorPanel[]>([]);
  const [activeMentorIds, setActiveMentorIds] = useState<string[]>([]);

  const { userId, sessionId } = useAppSelector((state) => state.session);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const homePageData = await fetchHomePageData(
          import.meta.env.VITE_PATHWAYS_ORG_ID,
        );
        setMentors(homePageData.mentors);
        setPanels(homePageData.panels);
        setActiveMentorIds(homePageData.activeMentorIds);
      } catch (error) {
        console.error("Failed to fetch home page data:", error);
      }
    };

    void fetchData();
  }, []);

  const updatedPanels: MentorPanel[] = useMemo(() => {
    if (!userId || !sessionId) return [];

    return panels.map((panel) => {
      const url = new URL(panel.panelUrl);
      url.searchParams.set("userid", userId);
      url.searchParams.set("sessionId", sessionId);
      url.searchParams.set("userEmail", getEmailFromUserId(userId));
      return { ...panel, panelUrl: url.toString() };
    });
  }, [panels, userId, sessionId]);

  const updatedMentors = useMemo<Mentor[]>(() => {
    if (!sessionId || !userId) return [];

    const excludedNames = [
      "Mark Hammel",
      "Amber Jolly",
      "William Purpura",
      "Jose Zavala",
      "Darla Holland, MD",
      "Susan Ramsey",
    ];

    const filteredMentors = mentors.filter(
      (mentor) => !excludedNames.includes(mentor.name),
    );

    const sortedOrderMentors = sortAndFilterByIdOrder(
      activeMentorIds,
      filteredMentors,
    );

    return sortedOrderMentors.map((mentor) => {
      const url = new URL(mentor.mentorUrl);
      url.searchParams.set("userid", userId);
      url.searchParams.set("sessionId", sessionId);
      url.searchParams.set("userEmail", getEmailFromUserId(userId));

      return {
        ...mentor,
        mentorUrl: url.toString(),
      };
    });
  }, [mentors, activeMentorIds, sessionId, userId]);

  return {
    mentors: updatedMentors,
    panels: updatedPanels,
  };
}
